import { useEffect, useState, type ReactNode } from 'react'
import { DIAL_CODES, parseE164 } from '@academy/config'
import { Field, Input, Select } from './Field.js'

interface PhoneFieldProps {
  /** Full E.164 value (`+<code><national>`) or '' when blank. */
  value: string
  /** Emits the combined E.164 string, or '' when national is empty / no code chosen. */
  onChange: (e164: string) => void
  label?: ReactNode
  hint?: ReactNode
  error?: ReactNode
  required?: boolean
  optional?: boolean
}

/**
 * Country-code selector + national-number input that combine into a single
 * E.164 string. There is NO default country code — the selector starts on a
 * disabled placeholder, so a number can't be saved without a code. The parent
 * stores/edits one `+<code><national>` string; this component splits it for
 * display (longest-prefix match) and recombines on every change.
 */
/** Split an incoming value into (code, national) for the local fields. A legacy
 * bare number (no +, no known code) yields no code — the national field shows the
 * digits and the user must pick a code (the intended "force complete" path). */
function splitValue(value: string): { code: string; national: string } {
  const parsed = parseE164(value)
  if (parsed) return parsed
  return { code: '', national: value.replace(/^\+/, '').replace(/\D/g, '') }
}

/** Recombine local fields into the E.164 string the parent stores, or '' while
 * incomplete (no code or no digits) so a number can't be saved without a code. */
function combine(code: string, national: string): string {
  const digits = national.replace(/\D/g, '')
  if (!digits || !code) return ''
  return `${code}${digits}`
}

export function PhoneField({
  value,
  onChange,
  label = 'Phone',
  hint,
  error,
  required,
  optional,
}: PhoneFieldProps) {
  // Local state so both fields accept input in any order. The parent's single
  // E.164 `value` can't drive them directly: while one half is missing the
  // combined value is '', which would otherwise wipe the half the user just typed.
  const [code, setCode] = useState(() => splitValue(value).code)
  const [national, setNational] = useState(() => splitValue(value).national)

  // Re-sync from `value` on an EXTERNAL change only (e.g. switching which parent
  // is edited, or a form reset). Our own onChange echoes back as `value`; ignore
  // that echo so it doesn't clobber in-progress typing.
  useEffect(() => {
    if (value === combine(code, national)) return
    const next = splitValue(value)
    setCode(next.code)
    setNational(next.national)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const update = (nextCode: string, nextNational: string) => {
    setCode(nextCode)
    setNational(nextNational)
    onChange(combine(nextCode, nextNational))
  }

  return (
    <Field label={label} hint={hint} error={error} required={required} optional={optional}>
      <div className="flex gap-2">
        <Select
          aria-label="Country code"
          className="w-40 shrink-0"
          value={code}
          onChange={(e) => update(e.target.value, national)}
        >
          <option value="" disabled>
            Code
          </option>
          {DIAL_CODES.map((dc) => (
            <option key={dc.iso} value={dc.code}>
              {dc.label}
            </option>
          ))}
        </Select>
        <Input
          type="tel"
          inputMode="numeric"
          className="flex-1"
          placeholder="Phone number"
          value={national}
          onChange={(e) => update(code, e.target.value)}
        />
      </div>
    </Field>
  )
}
